import React, { useMemo } from "react";
import { FCX } from "@datassential/platform-ui-lib";
import { sanitizeUrl } from "@helpers";
import "./MetricHeader.styles.scss";

export interface MetricColumnGroup {
    title: string | React.ReactNode;
    colSpan: number;
}

export interface MetricColumn {
    title: string;
    background?: string;
    color: string;
    link?: string;
    columnGroups: MetricColumnGroup[];
}

interface MetricHeaderProps {
    columns: MetricColumn[];
}

interface FlattenedColumnGroup extends MetricColumnGroup {
    color: string;
}

const MetricHeader: FCX<MetricHeaderProps> = ({ columns }) => {
    const flattenedColumnGroups = useMemo((): FlattenedColumnGroup[] => 
        columns.flatMap((metric) => 
            metric.columnGroups.map(group => ({ 
                ...group, 
                color: metric.color 
            }))
        ), 
        [columns]
    );

    const getTotalColSpan = (metric: MetricColumn) =>
        metric.columnGroups.reduce((total, group) => total + group.colSpan, 0);

    return (
        <div className="MetricHeader">
            <div className="MetricHeader__metrics-row">
                {columns.map((metric, index) => (
                    <div
                        key={`metric-${index}`}
                        className="MetricHeader__cell--metric"
                        style={{ 
                            background: metric.background,
                            gridColumn: `span ${getTotalColSpan(metric)}`
                        }}
                    >
                        {metric.link ? (
                            <a
                                className="MetricHeader__link"
                                href={sanitizeUrl(metric.link)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {metric.title}
                            </a>
                        ) : (
                            metric.title
                        )}
                    </div>
                ))}
            </div>

            <div className="MetricHeader__groups-row">
                {flattenedColumnGroups.map((columnGroup, index) => (
                    <div
                        key={`group-${index}`}
                        className="MetricHeader__cell--group"
                        style={{ 
                            color: columnGroup.color,
                            gridColumn: `span ${columnGroup.colSpan}`
                        }}
                    >
                        {columnGroup.title}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(MetricHeader);
